import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WifiOff, Phone, Mail } from 'lucide-react';

export const OfflineOverlay = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <AnimatePresence>
      {isOffline && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 text-center overflow-hidden relative"
          >
            {/* Background decoration */}
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-brand-50 to-brand-100/50 -z-10" />
            
            <div className="mx-auto w-24 h-24 mb-6 rounded-2xl bg-white shadow-lg p-2 relative">
              <img 
                src="/logo.jpg" 
                alt="Smart School Flow" 
                className="w-full h-full object-contain rounded-xl"
              />
              <div className="absolute -bottom-2 -right-2 bg-red-500 text-white p-2 rounded-full border-4 border-white shadow-sm">
                <WifiOff className="w-4 h-4" />
              </div>
            </div>

            <h2 className="text-2xl font-bold text-slate-900 mb-3 font-display">
              We Lost Connection
            </h2>
            
            <p className="text-slate-600 mb-8 leading-relaxed">
              It looks like you're offline. Take a deep breath, your data is safe! 
              We'll automatically reconnect as soon as your internet returns. 
              In the meantime, feel free to reach out if you need urgent assistance.
            </p>

            <div className="bg-slate-50 rounded-2xl p-6 mb-8 text-left border border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900 mb-4 uppercase tracking-wider">Contact Support</h3>
              <div className="grid grid-cols-2 gap-4">
                <a href="tel:0786014910" className="flex flex-col items-center justify-center gap-2 bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-brand-300 hover:shadow-md transition-all group">
                  <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 group-hover:scale-110 transition-transform">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div className="text-sm font-medium text-slate-900">Call Support</div>
                  <div className="text-xs text-slate-500 font-medium">Click to dial</div>
                </a>
                
                <a href="mailto:kanyamualexis@gmail.com" className="flex flex-col items-center justify-center gap-2 bg-white rounded-xl p-4 shadow-sm border border-slate-200 hover:border-brand-300 hover:shadow-md transition-all group">
                  <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 group-hover:scale-110 transition-transform">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div className="text-sm font-medium text-slate-900">Email Support</div>
                  <div className="text-xs text-slate-500 font-medium">Click to write</div>
                </a>
              </div>
            </div>

            <button 
              onClick={() => window.location.reload()}
              className="w-full py-3.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-medium transition-colors"
            >
              Try Reconnecting
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
